import { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useFeesContext } from 'sonata-platform-components';
import { type ValidationErrors, validationErrorChecker } from 'sonata-react-components';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { getLineItemDraftSchema, getLineItemSchema } from '@/validators/entities.validator';
import { reconcileFieldError } from '@/validators/formValidation.helper';

import type { Layer } from 'sonata-static-storage/lib/common/common.types';
import type { InsertionOrder, LineItem } from 'sonata-static-storage/lib/entities';
import type { LineItemGoal } from 'sonata-static-storage/lib/entities/lineItem/lineItem.types';
import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';
import type { SchedulePartingInterface } from 'sonata-static-storage/lib/schemas/campaign/scheduleParting.schema';
import type { FrequencyCapping } from 'sonata-static-storage/lib/schemas/multiFlight/frequencyCapping.schema';
import type { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';
import type { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { Zone } from 'sonata-static-storage/lib/schemas/zone/zone.schema';
import type { ListType } from '@/components/common/MultiFlightMap/multiFlightMap.types';
import type { FormSection, FormSubsection } from '@/types/formSections.types';

type Errors = {
  [key: string]: Errors;
} & ValidationErrors;

type FormState = {
  activeSubsection: string | null;
  selectedSection: FormSection | null;
};

type LIState = {
  ads?: LineItem['ads'];
  adsDistribution?: LineItem['adsDistribution'];
  adsPriceEnabled?: LineItem['adsPriceEnabled'];
  beginDate?: LineItem['beginDate'];
  countries?: string[];
  dco?: InsertionOrder['dco'];
  endDate?: LineItem['endDate'];
  errors: Errors;
  goals: LineItemGoal[];
  hasAttemptedDraftValidation: boolean;
  hasAttemptedValidation: boolean;
  isDistributionActive: boolean;
  isSnliActive: boolean;
  list: ListType;
  mediaType?: LineItem['mediaType'];
  name?: string;
  pricingModelConfig?: PricingModelConfig;
  scheduleParting?: SchedulePartingInterface;
  screenGroupsLayer?: Layer | null;
  screenInventoryExportIds: string[];
  screens?: GeoDataAndScreensInteface[];
  selectedSection: FormSection | null;
  targeting?: TargetingBaseType;
  timezone: LineItem['timezone'];
  userFrequencyCapping?: FrequencyCapping;
  zones?: Zone[];
};

type Context = {
  addScreenInventoryExportId: (exportId: string) => void;
  getDraftValidationErrors: () => ValidationErrors | undefined;
  getFormErrors: () => ValidationErrors;
  getScreens: () => GeoDataAndScreensInteface[] | null;
  getZones: () => Zone[] | null;
  shouldShowValidationErrors: () => boolean;
  setActiveSubsection: (activeSubsection: FormSubsection | null) => void;
  setAds: (ads: LineItem['ads']) => void;
  setAdsDistribution: (newValue: LineItem['adsDistribution']) => void;
  setAdsPriceEnabled: (newValue: LineItem['adsPriceEnabled']) => void;
  setBeginDate: (beginDate: LineItem['beginDate']) => void;
  setCountries: (countries: string[]) => void;
  setDco: (dco: InsertionOrder['dco']) => void;
  setEndDate: (endDate: LineItem['endDate']) => void;
  setGoal: (newGoal: LineItemGoal) => void;
  setList: (list: ListType) => void;
  setMediaType: (mediaType: LineItem['mediaType']) => void;
  setName: (name: string) => void;
  setPricingModelConfig: (pricingModelConfig: PricingModelConfig) => void;
  setScheduleParting: (scheduleParting: SchedulePartingInterface) => void;
  setScreenGroupsLayer: (layer?: Layer | null) => void;
  setScreens: (screens: GeoDataAndScreensInteface[]) => void;
  setIsSnliActive: (active: boolean) => void;
  setSelectedSection: (section: FormSection | null) => void;
  setTargeting: (targeting: TargetingBaseType) => void;
  setUserFrequencyCapping: (frequencyCapping: FrequencyCapping) => void;
  setZones: (zones: Zone[]) => void;
  validateForm: (draft?: boolean) => ValidationErrors;
} & FormState &
  LIState;

type Props = {
  defaultCountries?: string[];
  defaultLineItem?: LineItem;
};

const LineItemFormContext = createContext<Context | null>(null);

export function LineItemFormProvider({ children, defaultCountries, defaultLineItem }: PropsWithChildren<Props>) {
  const { formatMessage } = useIntl();
  const { minimumBidPrices } = useFeesContext();

  const [state, setState] = useState<LIState & FormState>({
    countries: defaultCountries,
    ...defaultLineItem,
    activeSubsection: 'mediaChannel',
    errors: {},
    goals: defaultLineItem?.goals || [],
    hasAttemptedDraftValidation: false,
    hasAttemptedValidation: false,
    isDistributionActive:
      defaultLineItem?.ads?.some(ad => !ad.deleted && ad.visible && (ad.distribution || 0) > 0) || false,
    isSnliActive: false,
    list: defaultLineItem?.type === CampaignFamilyEnum.dooh ? 'screens' : 'locations',
    screenGroupsLayer: null,
    screenInventoryExportIds: [],
    selectedSection: null,
    timezone: defaultLineItem?.timezone || ''
  });

  const validator = getLineItemSchema(formatMessage, {
    allZonesRetargeting:
      Array.isArray(state.zones) && state.zones.length > 0 && state.zones.every(z => z.retargetingType === 'only'),
    beginDate: state.beginDate,
    bidModels: state.pricingModelConfig?.bidModels,
    endDate: state.endDate,
    isDistributionActive: state.isDistributionActive,
    minimumBidPrices,
    type: defaultLineItem?.type
  });

  const setValue = (key: keyof LIState, value: any, withValidation = false) => {
    setState(prev => {
      const newState = {
        ...prev,
        [key]: value
      };
      if (withValidation) {
        const fieldValidation = validationErrorChecker(newState, validator)?.[key];
        if (!fieldValidation) {
          delete prev.errors[key];
        }
        return {
          ...newState,
          errors: {
            ...prev.errors,
            ...(fieldValidation && { [key]: fieldValidation })
          }
        };
      }
      return newState;
    });
  };

  const setSelectedSection = (selectedSection: FormSection | null): void => {
    setState(prev => ({
      ...prev,
      selectedSection
    }));
  };

  const setActiveSubsection = (activeSubsection: FormSubsection | null): void => {
    setState(prev => ({
      ...prev,
      activeSubsection
    }));
  };

  const setList = (list: ListType): void => {
    setState(prev => ({
      ...prev,
      list
    }));
  };

  const setDco = (dco: InsertionOrder['dco']): void => {
    setState(prev => ({
      ...prev,
      dco
    }));
  };

  const setAds = (ads: LineItem['ads']): void => {
    setState(prev => {
      const isDistributionActive = ads.some(ad => !ad.deleted && ad.visible && (ad.distribution || 0) > 0);
      const newState = {
        ...prev,
        ads,
        isDistributionActive
      };

      const freshValidator = getLineItemSchema(formatMessage, {
        beginDate: newState.beginDate,
        bidModels: newState.pricingModelConfig?.bidModels,
        endDate: newState.endDate,
        isDistributionActive: newState.isDistributionActive,
        minimumBidPrices,
        type: defaultLineItem?.type
      });
      return reconcileFieldError(newState, 'ads', freshValidator);
    });
  };

  const setZones = (zones: Zone[]): void => {
    setState(prev => ({
      ...prev,
      zones
    }));
  };

  const addScreenInventoryExportId = (exportId: string) => {
    setState(prev => ({
      ...prev,
      screenInventoryExportIds: [...prev.screenInventoryExportIds, exportId]
    }));
  };

  const setScreenGroupsLayer = (layer?: Layer | null) => {
    setState(prev => ({
      ...prev,
      screenGroupsLayer: layer || null
    }));
  };

  const setScreens = (screens: GeoDataAndScreensInteface[]): void => {
    setState(prev => ({
      ...prev,
      screens
    }));
  };

  const setGoal = (newGoal: LineItemGoal): void => {
    // Only one non-budget goal is allowed per timeFrame, so switching e.g. daily
    // clicks -> daily impressions must replace the previous goal, not add to it.
    const newGoals = structuredClone(state.goals || []).filter(({ timeFrame, type }) => {
      if (timeFrame !== newGoal.timeFrame) {
        return true;
      }
      if (type === 'budget' || newGoal.type === 'budget') {
        return type !== newGoal.type;
      }
      return false;
    });
    if (newGoal.target) {
      newGoals.push(newGoal);
    }
    setState(prev => ({
      ...prev,
      goals: newGoals
    }));
  };

  // Saving a draft only requires a subset of the fields; publishing requires
  // them all (it flips `completed` to true).
  const buildFormValidator = (draft: boolean) =>
    draft ? getLineItemDraftSchema(formatMessage, { minimumBidPrices, type: defaultLineItem?.type }) : validator;

  const getFormErrors: Context['getFormErrors'] = () => {
    return validationErrorChecker(state, validator);
  };

  const getZones = (): Zone[] | null => {
    return state.zones || null;
  };

  const getScreens = (): GeoDataAndScreensInteface[] | null => {
    return state.screens || null;
  };

  const validateForm = (draft = false): ValidationErrors => {
    const errors = validationErrorChecker(state, buildFormValidator(draft)) || {};
    setState(prev => {
      return {
        ...prev,
        errors,
        // Only attempting to publish turns the sidebar's publish-level errors
        // on: a draft save validates less, so it must not light them up. Once
        // a publish attempt has happened they stay on.
        hasAttemptedDraftValidation: prev.hasAttemptedDraftValidation || draft,
        hasAttemptedValidation: prev.hasAttemptedValidation || !draft
      };
    });
    return errors;
  };

  // A draft-only attempt shows the sidebar the errors that blocked the draft save, not the publish-level ones
  const getDraftValidationErrors: Context['getDraftValidationErrors'] = () => {
    return state.hasAttemptedDraftValidation && !state.hasAttemptedValidation
      ? validationErrorChecker(state, buildFormValidator(true))
      : undefined;
  };

  const shouldShowValidationErrors: Context['shouldShowValidationErrors'] = () => {
    return state.hasAttemptedValidation;
  };

  const providerValue = useMemo(
    () => ({
      ...state,
      addScreenInventoryExportId,
      getDraftValidationErrors,
      getFormErrors,
      getScreens,
      getZones,
      setActiveSubsection,
      setAds,
      setAdsDistribution: (value: Context['adsDistribution']) => setValue('adsDistribution', value),
      setAdsPriceEnabled: (value: Context['adsPriceEnabled']) => setValue('adsPriceEnabled', value),
      setBeginDate: (value: Context['beginDate']) => setValue('beginDate', value),
      setCountries: (value: Context['countries']) => setValue('countries', value),
      setDco,
      setEndDate: (value: Context['endDate']) => setValue('endDate', value),
      setGoal,
      setIsSnliActive: (value: Context['isSnliActive']) => setValue('isSnliActive', value),
      setList,
      setMediaType: (value: Context['mediaType']) => setValue('mediaType', value),
      setName: (value: Context['name']) => setValue('name', value),
      setPricingModelConfig: (value: Context['pricingModelConfig']) => setValue('pricingModelConfig', value),
      setScheduleParting: (value: Context['scheduleParting']) => setValue('scheduleParting', value),
      setScreenGroupsLayer,
      setScreens,
      setSelectedSection,
      setTargeting: (value: Context['targeting']) => setValue('targeting', value),
      setUserFrequencyCapping: (value: Context['userFrequencyCapping']) => setValue('userFrequencyCapping', value),
      setZones,
      shouldShowValidationErrors,
      validateForm
    }),
    [state]
  );

  return <LineItemFormContext.Provider value={providerValue}>{children}</LineItemFormContext.Provider>;
}

export function useLineItemFormContext(): Context {
  const context = useContext(LineItemFormContext);
  if (!context) {
    throw new Error('useLineItemFormContext must be used within a LineItemFormProvider');
  }
  return context;
}
